import {
  Contract,
  PossibilitiesDocuments,
} from '$modules/personal/customer-card/models/card-aplication';
import { ContractApplication } from '$modules/personal/customer-card/models/contract';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { AccountCards } from '../models/account';
import { BonusCardLink, Card, ValidationIbanResponse } from '../models/card';
import { CardStorageService } from './card-storage.service';
import { WorkplaceListResponse } from './workplace-list-response';

export type RequestParts = 'organization' | 'addresses' | 'bank-account';

export interface FilialInfo {
  code: string;
  name: string;
  trn: string;
}

export interface LegalNameInfo {
  created_at: string;
  name: string;
  short_name: string;
  status: string;
}

@Injectable({
  providedIn: 'root',
})
export class CorporateCardService {
  private readonly http = inject(HttpClient);
  private readonly cardStorageService = inject(CardStorageService);

  private readonly url = '/v1/corporate-card';
  private readonly requestsUrl = this.url + '/requests';
  private readonly contractsUrl = this.url + '/contracts';
  private readonly cardUrl = this.url + '/cards';
  private readonly documentsUrl = this.url + '/documents';
  private readonly accountsUrl = this.url + '/accounts';
  private readonly workplacesUrl = this.url + '/workplaces';

  public getContract(): Observable<Contract> {
    return this.http.get<Contract>(this.contractsUrl + '/current').pipe(
      tap((res) => {
        this.cardStorageService.contract = res;
        this.cardStorageService.application =
          res.applications[res.applications.length - 1];
      })
    );
  }

  public deleteDraft(): Observable<{ status: 'ok' }> {
    return this.http.delete<{ status: 'ok' }>(
      `${this.contractsUrl}/application/draft-delete`
    );
  }

  public getCountContract(): Observable<number> {
    return this.http.get<number>(this.contractsUrl + '/count');
  }

  public sendApplicationType(params: Params): Observable<ContractApplication> {
    return this.http
      .post<ContractApplication>(this.contractsUrl + '/application/add', params)
      .pipe(
        filter((val) => !!val),
        tap((res) => {
          this.cardStorageService.application$.next(res);
          this.cardStorageService.application = res;
        })
      );
  }

  public updateContract<T>(params: Params): Observable<T> {
    return this.http.patch<T>(this.contractsUrl + '/application', params);
  }

  public sendContract(params?: Params) {
    return this.http.post(this.contractsUrl + '/application/send', params).pipe(
      tap(() => {
        this.cardStorageService.application$.next(null);
        this.cardStorageService.application = null;
      })
    );
  }

  public downloadDocument(documentId: string) {
    return this.http.get(
      `${this.contractsUrl}/application/document/${documentId}`,
      {
        responseType: 'arraybuffer',
        observe: 'response',
      }
    );
  }

  public getAdditionalAgreementDocument(
    body: PossibilitiesDocuments
  ): Observable<any> {
    return this.http
      .post(this.documentsUrl, body, {
        responseType: 'arraybuffer',
        observe: 'response',
      })
      .pipe(
        tap((res) => {
          const blob = new Blob([res.body], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);

          window.open(url, '_blank');

          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        })
      );
  }

  public saveTab(type: RequestParts, params: Params) {
    return this.http.post(`${this.requestsUrl}/${type}`, params);
  }

  public addRequestCard(params?: Params) {
    return this.http.post(this.requestsUrl, params);
  }

  public validateIban(iban: string) {
    return this.http.get<ValidationIbanResponse>(
      `${this.requestsUrl}/bank-account/iban/${iban}`
    );
  }

  public getAccounts(params?: Params) {
    return this.http.get<AccountCards>(this.accountsUrl, params).pipe(
      tap((res) => {
        this.cardStorageService.account = res;
      })
    );
  }

  public createCard(params?: Params) {
    return this.http.post<Card>(this.cardUrl, params);
  }

  public updateCard(id: number, params?: Params) {
    return this.http.put<Card>(`${this.cardUrl}/${id}`, params);
  }

  public getCard(id: number | string, params?: Params) {
    return this.http.get<Card>(`${this.cardUrl}/${id}`, params);
  }

  public getBonusCard(id: number | string) {
    return this.http.get<BonusCardLink>(`${this.cardUrl}/${id}/bonus`);
  }

  public deleteCard(id: number, options: { [k in string]: any }) {
    return this.http.delete(`${this.cardUrl}/${id}`, options);
  }

  public getWorkplaces(params?: Params): Observable<WorkplaceListResponse> {
    let httpParams = new HttpParams();

    if (params) {
      httpParams = httpParams.set('date_from', params.date_from);
      httpParams = httpParams.set('date_to', params.date_to);
    }

    return this.http.get<WorkplaceListResponse>(this.workplacesUrl, {
      params: httpParams,
    });
  }

  public getBanks(params?: Params) {
    return this.http.get(`/v1/corporate-card/banks`, params);
  }

  public transfer(id: number, params: Params) {
    return this.http.post<Card>(`/v1/corporate-card/transfers/${id}`, params);
  }

  public getUnp() {
    return this.http.get<LegalNameInfo>(`/v1/corporate-card/unp`);
  }

  public getFilialName(code: string, trn: string) {
    return this.http.get<FilialInfo>(`/v1/filials/${code}/${trn}`);
  }
}
